import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from '../../hooks/axios';
import { Vehicle } from '../../types/vehicle';
import PageBreadcrumb from '../../components/common/PageBreadCrumb';
import Input from '../../components/form/input/InputField';
import { vehicleTypeLabel } from '../../utils/vehicleTypeLabel';

interface SortConfig {
  key: keyof Vehicle | '';
  direction: 'asc' | 'desc';
}

interface VehicleTypeOption {
  id: string;
  name?: string | null;
  description: string;
}

const VehicleList: React.FC = () => {
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [vehicleTypes, setVehicleTypes] = useState<Record<string, VehicleTypeOption>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [totalPages, setTotalPages] = useState(1);
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: '', direction: 'asc' });
  const [paginatedVehicles, setPaginatedVehicles] = useState<Vehicle[]>([]);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    handleFilterSortPaginate();
  }, [searchTerm, currentPage, pageSize, sortConfig, vehicles.length, vehicleTypes]);

  const getVehicleTypeName = (vehicleTypeId: string | null | undefined) => {
    if (!vehicleTypeId) return '—';
    const type = vehicleTypes[vehicleTypeId];
    return type ? vehicleTypeLabel(type) : '—';
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [vehiclesRes, typesRes] = await Promise.all([
        axios.get<Vehicle[]>('/vehicles'),
        axios.get<VehicleTypeOption[]>('/vehicletypes'),
      ]);
      setVehicles(vehiclesRes.data);
      setVehicleTypes(
        typesRes.data.reduce<Record<string, VehicleTypeOption>>((acc, type) => {
          acc[type.id] = type;
          return acc;
        }, {}),
      );
    } catch {
      setError('Failed to fetch vehicles');
    } finally {
      setLoading(false);
    }
  };

  const normalizeSearchValue = (value: unknown) =>
    String(value ?? '').toLowerCase();

  const handleFilterSortPaginate = () => {
    const term = searchTerm.toLowerCase();
    let filtered = vehicles.filter((vehicle) => {
      const legacy = vehicle as Vehicle & { name?: string; vehicleNo?: string };
      return (
        normalizeSearchValue(legacy.registration).includes(term) ||
        normalizeSearchValue(legacy.make).includes(term) ||
        normalizeSearchValue(legacy.model).includes(term) ||
        normalizeSearchValue(getVehicleTypeName(legacy.vehicleTypeId)).includes(term) ||
        normalizeSearchValue(legacy.vehicleNo).includes(term) ||
        normalizeSearchValue(legacy.name).includes(term)
      );
    });

    if (sortConfig.key !== '') {
      filtered = filtered.sort((a, b) => {
        const aValue =
          sortConfig.key === 'vehicleTypeId'
            ? getVehicleTypeName(a.vehicleTypeId)
            : String(a[sortConfig.key as keyof Vehicle] ?? '');
        const bValue =
          sortConfig.key === 'vehicleTypeId'
            ? getVehicleTypeName(b.vehicleTypeId)
            : String(b[sortConfig.key as keyof Vehicle] ?? '');
        return sortConfig.direction === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      });
    }

    const totalItems = filtered.length;
    const calculatedTotalPages = Math.max(1, Math.ceil(totalItems / pageSize));
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    setTotalPages(calculatedTotalPages);
    setPaginatedVehicles(filtered.slice(startIndex, endIndex));
  };

  const handleSort = (key: keyof Vehicle) => {
    setSortConfig((current) => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const renderSortIcon = (key: keyof Vehicle) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === 'asc' ? ' ↑' : ' ↓';
  };

  const renderPagination = () => {
    const pages = [];
    for (let i = 1; i <= totalPages; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => setCurrentPage(i)}
          className={`px-3 py-1 mx-1 rounded ${
            currentPage === i
              ? 'bg-brand-500 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          {i}
        </button>
      );
    }
    return (
      <div className="flex justify-center mt-4">
        <button
          onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
          disabled={currentPage === 1}
          className="px-3 py-1 mx-1 rounded bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-50"
        >
          Previous
        </button>
        {pages}
        <button
          onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
          disabled={currentPage === totalPages}
          className="px-3 py-1 mx-1 rounded bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-50"
        >
          Next
        </button>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <PageBreadcrumb pageTitle="Vehicles" />

      <div className="flex flex-wrap items-center justify-end gap-3">
        <Link
          to="/vehicles/add"
          className="inline-flex items-center justify-center rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-600"
        >
          Add Vehicle
        </Link>
      </div>

      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-theme-sm dark:border-gray-800 dark:bg-white/[0.03]">
        <div className="p-4 border-b">
          <Input
            placeholder="Search by registration, make, model, or type..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full"
          />
        </div>

        {error && (
          <div className="border-b border-error-200 bg-error-50 px-4 py-3 text-sm text-error-700 dark:border-error-500/30 dark:bg-error-500/10 dark:text-error-400">
            {error}
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-800 dark:divide-gray-800">
            <thead className="bg-gray-50 dark:bg-white/[0.02]">
              <tr>
                <th
                  className="px-5 py-3 text-start text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 cursor-pointer"
                  onClick={() => handleSort('registration')}
                >
                  Registration {renderSortIcon('registration')}
                </th>
                <th
                  className="px-5 py-3 text-start text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 cursor-pointer"
                  onClick={() => handleSort('make')}
                >
                  Make {renderSortIcon('make')}
                </th>
                <th
                  className="px-5 py-3 text-start text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 cursor-pointer"
                  onClick={() => handleSort('model')}
                >
                  Model {renderSortIcon('model')}
                </th>
                <th
                  className="px-5 py-3 text-start text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 cursor-pointer"
                  onClick={() => handleSort('vehicleTypeId')}
                >
                  Type {renderSortIcon('vehicleTypeId')}
                </th>
                <th
                  className="px-5 py-3 text-start text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 cursor-pointer"
                  onClick={() => handleSort('status')}
                >
                  Status {renderSortIcon('status')}
                </th>
                <th className="px-5 py-3 text-end text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-800 dark:bg-transparent">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-4 text-center">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-500"></div>
                    </div>
                  </td>
                </tr>
              ) : paginatedVehicles.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                    No vehicles found
                  </td>
                </tr>
              ) : (
                paginatedVehicles.map((vehicle) => (
                  <tr key={vehicle.id}>
                    <td className="px-5 py-4 text-sm text-gray-900 dark:text-white/90">{vehicle.registration || '—'}</td>
                    <td className="px-5 py-4 text-sm text-gray-900 dark:text-white/90">{vehicle.make || '—'}</td>
                    <td className="px-5 py-4 text-sm text-gray-900 dark:text-white/90">{vehicle.model || '—'}</td>
                    <td className="px-5 py-4 text-sm text-gray-900 dark:text-white/90">
                      {getVehicleTypeName(vehicle.vehicleTypeId)}
                    </td>
                    <td className="px-5 py-4 text-sm text-gray-900 dark:text-white/90">
                      {vehicle.status ? 'Active' : 'Inactive'}
                    </td>
                    <td className="px-5 py-4 text-sm text-gray-900 dark:text-white/90 text-right">
                      <div className="flex items-center justify-end gap-3">
                        <Link
                        to={`/vehicles/${vehicle.id}/view`} className="text-brand-600 hover:text-brand-700 hover:underline dark:text-brand-400"
                      >
                        View
                      </Link>
                      <Link
                        to={`/vehicles/${vehicle.id}`} className="text-green-600 hover:underline"
                      >
                        Edit
                      </Link>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {!loading && paginatedVehicles.length > 0 && (
          <div className="px-6 py-4 border-t">
            <div className="flex justify-between items-center">
              <div>
                <select
                  value={pageSize}
                  onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
                  className="border rounded px-2 py-1"
                >
                  <option value={5}>5 per page</option>
                  <option value={10}>10 per page</option>
                  <option value={20}>20 per page</option>
                  <option value={50}>50 per page</option>
                </select>
              </div>
              {renderPagination()}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VehicleList;
